import { Card } from '@radix-ui/themes'
import { TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description: string
  confirmLabel?: string
  variant?: 'danger' | 'warning'
}

const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  variant = 'warning'
}: ConfirmDialogProps) => {
  const { t } = useTranslation()
  const isDanger = variant === 'danger'

  const handleConfirm = () => {
    onClose()
    onConfirm()
  }

  return (
    <div
      className="fixed inset-0 bg-opacity-50 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50"
      style={{ display: open ? 'block' : 'none' }}
    >
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <Card className="shadow-xl bg-card w-full max-w-md justify-center relative">
          <div className="flex flex-col gap-6 p-4 place-self-center">
            <div className="flex flex-col gap-3 text-center items-center">
              <div
                className={`rounded-full p-4 w-fit border ${
                  isDanger
                    ? 'bg-red-500/10 border-red-500/30'
                    : 'bg-primary/10 border-primary/30'
                }`}
              >
                <TriangleAlert
                  className={`w-6 h-6 ${isDanger ? 'text-red-500' : 'text-primary'}`}
                />
              </div>

              <h2 className="text-text-main text-2xl font-bold">{title}</h2>

              <p className="text-text-secondary text-sm leading-relaxed whitespace-pre-line">
                {description}
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <button
                onClick={handleConfirm}
                className={
                  isDanger
                    ? 'bg-red-500 text-white font-bold py-3.5 rounded-full'
                    : 'btn-gradient-fill rounded-full py-3.5'
                }
              >
                <p className="font-bold">
                  {confirmLabel ?? t('dialogs.confirm')}
                </p>
              </button>
              <button
                onClick={onClose}
                className="text-text-secondary text-sm py-2"
              >
                {t('dialogs.cancel')}
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default ConfirmDialog
